import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface StyledMultiFileInputProps {
  id: string;
  accept: string;
  value: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  hint?: string;
  className?: string;
  /** Refuses to hold more than this many, matching the server's batch cap. */
  maxFiles?: number;
}

/**
 * Several files at once, with each one removable before submitting.
 *
 * A SIBLING of StyledFileInput rather than a `multiple` flag on it. That
 * component's contract is `value: File | null` across 22 call sites, none of
 * which want multi-select; widening it to `File | File[] | null` would make
 * every one of them handle a shape they never produce, to serve one caller.
 *
 * Appends rather than replaces on each pick: a consumer choosing nine
 * screenshots from two folders should not lose the first batch by opening the
 * picker again — which is the tedium this whole feature exists to remove.
 */
export function StyledMultiFileInput({
  id,
  accept,
  value,
  onChange,
  disabled = false,
  label,
  hint,
  className,
  maxFiles,
}: StyledMultiFileInputProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerInput = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (picked.length === 0) return;
      const next = [...value, ...picked];
      onChange(maxFiles != null ? next.slice(0, maxFiles) : next);
      // Cleared so picking the SAME file again still fires a change event.
      if (inputRef.current) inputRef.current.value = "";
    },
    [value, onChange, maxFiles],
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const hintId = hint ? `${id}-hint` : undefined;
  const atCapacity = maxFiles != null && value.length >= maxFiles;

  return (
    <div className={cn("space-y-2", className)}>
      {label != null && (
        <Label htmlFor={id} className="text-sm font-medium text-muted-foreground">
          {label}
        </Label>
      )}

      <input
        ref={inputRef}
        id={id}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        disabled={disabled || atCapacity}
        onChange={handleChange}
        aria-describedby={hintId}
      />

      <button
        type="button"
        onClick={triggerInput}
        disabled={disabled || atCapacity}
        className={cn(
          "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border",
          "bg-muted/30 px-4 py-6 text-sm text-muted-foreground transition-colors",
          "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <Upload className="size-4 shrink-0" aria-hidden />
        {atCapacity
          ? t("components.styledMultiFileInput.atCapacity", { count: maxFiles ?? 0 })
          : t("components.styledMultiFileInput.choose")}
      </button>

      {/* Named and removable. A count alone ("9 archivos") gives no way to
          drop the one picked by mistake except starting over. */}
      {value.length > 0 ? (
        <ul className="space-y-1">
          {value.map((f, i) => (
            <li
              key={`${f.name}-${f.size}-${i}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
            >
              <span className="min-w-0 truncate text-sm text-foreground">{f.name}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={disabled}
                aria-label={t("components.styledMultiFileInput.remove", { name: f.name })}
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <X className="size-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {hint != null ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
